import React from 'react';
import { ReportFormData } from '../types/survey';
import './ReportModal.css';

interface ReportViewModalProps {
	isOpen: boolean;
	onClose: () => void;
	reportData: ReportFormData;
	surveyId?: string;
	riskScore?: number;
	timestamp?: string;
	report?: any;
}

export const ReportViewModal: React.FC<ReportViewModalProps> = ({ isOpen, onClose, reportData, surveyId, riskScore, timestamp, report }) => {
	if (!isOpen) return null;

	const formatDate = (dateString: string) => {
		if (!dateString) return '';
		try {
			return new Date(dateString).toLocaleDateString('sr-RS', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			});
		} catch (error) {
			return dateString;
		}
	};

	const formatTimestamp = (timestampString?: string) => {
		if (!timestampString) return '';
		try {
			return new Date(timestampString).toLocaleString('sr-RS');
		} catch (error) {
			return timestampString;
		}
	};

	return (
		<div className='report-modal-overlay'>
			<div className='report-modal'>
				<div className='report-modal-header'>
					<h2>Pregled prijave</h2>
					<p className='report-modal-subtitle'>Informacije o potencijalnoj žrtvi trgovine ljudima</p>

					{timestamp && (
						<p className='report-modal-subtitle'>
							Datum kreiranja: <strong>{formatTimestamp(timestamp)}</strong>
						</p>
					)}
					<button className='report-modal-close' onClick={onClose}>
						×
					</button>
				</div>

				<div className='report-modal-content'>
					<div className='report-form-view'>
						<div className='form-section'>
							<h3>Osnovni podaci</h3>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Ime:</label>
									<span className='field-value'>{reportData.name || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Prezime:</label>
									<span className='field-value'>{reportData.surname || 'Nije uneseno'}</span>
								</div>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Datum rođenja:</label>
									<span className='field-value'>{formatDate(reportData.dateOfBirth) || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Rod:</label>
									<span className='field-value'>{reportData.gender || 'Nije uneseno'}</span>
								</div>
							</div>

							<div className='form-group-view full-width'>
								<label>Adresa:</label>
								<span className='field-value'>{reportData.address || 'Nije uneseno'}</span>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Država:</label>
									<span className='field-value'>{reportData.country || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Kontakt:</label>
									<span className='field-value'>{reportData.contact || 'Nije uneseno'}</span>
								</div>
							</div>
						</div>
						<div className='form-section'>
							<h3>Status prijave</h3>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Prihvaćena:</label>
									<span className='field-value'>{report?.decision == null ? 'Na čekanju' : report?.decision ? 'Da' : 'Ne'}</span>
								</div>
								<div className='form-group-view'>
									<label>Osoba pristala da bude korisnik centra:</label>
									<span className='field-value'>{report?.enrolled == null ? '-' : report?.enrolled ? 'Da' : 'Ne'}</span>
								</div>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Obradio:</label>
									<span className='field-value'>{report?.completedBy || '-'}</span>
								</div>
								<div className='form-group-view'>
									<label>Razlog:</label>
									<span className='field-value'>{report?.reason ?? '-'}</span>
								</div>
							</div>
						</div>

						<div className='form-section mt-3'>
							<h3>Status informacije</h3>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Bračni status:</label>
									<span className='field-value'>{reportData.maritalStatus || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Sa kime boravi:</label>
									<span className='field-value'>{reportData.stayingWithType || 'Nije uneseno'}</span>
								</div>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Lokacija gde se trenutno nalazi:</label>
									<span className='field-value'>{reportData.placeWhereLives || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Adresa lokacije:</label>
									<span className='field-value'>{reportData.locationAddress || 'Nije uneseno'}</span>
								</div>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Poseban status:</label>
									<span className='field-value'>{reportData.specialStatus || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Zdravstveni status:</label>
									<span className='field-value'>{reportData.healthStatus || 'Nije uneseno'}</span>
								</div>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Oblik eksploatacije:</label>
									<span className='field-value'>{reportData.exploitationType || 'Nije uneseno'}</span>
								</div>
								<div className='form-group-view'>
									<label>Da li eksploatacija traje:</label>
									<span className='field-value'>{reportData.exploitationPhase || 'Nije uneseno'}</span>
								</div>
							</div>

							{reportData.explanation && (
								<div className='form-group-view full-width'>
									<label>Obrazloženje:</label>
									<div className='field-value-text'>{reportData.explanation}</div>
								</div>
							)}
						</div>

						<div className='form-section mt-3'>
							<h3>Usluge i oblici podrške</h3>

							<div className='form-group-view full-width'>
								<label>Potrebne usluge i oblici podrške:</label>
								<span className='field-value'>{reportData.neededSupport || 'Nije uneseno'}</span>
							</div>

							{reportData.neededSupportDetails && (
								<div className='form-group-view full-width'>
									<label>Detalji potrebne podrške:</label>
									<div className='field-value-text'>{reportData.neededSupportDetails}</div>
								</div>
							)}

							<div className='form-group-view full-width'>
								<label>Pružene usluge i oblici podrške:</label>
								<span className='field-value'>{reportData.givenSupport || 'Nije uneseno'}</span>
							</div>

							{reportData.givenSupportDetails && (
								<div className='form-group-view full-width'>
									<label>Detalji pružene podrške:</label>
									<div className='field-value-text'>{reportData.givenSupportDetails}</div>
								</div>
							)}

							{reportData.additionalNotes && (
								<div className='form-group-view full-width'>
									<label>Dodatne napomene:</label>
									<div className='field-value-text'>{reportData.additionalNotes}</div>
								</div>
							)}
						</div>

						{surveyId && (
							<div className='form-section mt-3'>
								<h3>Informacije o anketi</h3>
								<div className='form-group-view'>
									<label>ID ankete:</label>
									<span className='field-value'>{surveyId}</span>
								</div>
							</div>
						)}
					</div>

					<div className='form-actions'>
						<button type='button' className='btn btn-primary' onClick={onClose}>
							Zatvori
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
